import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Award, Zap, Heart, Globe, Star, Briefcase } from 'lucide-react';

const team = [
  { 
    name: 'K.Gangadhar', 
    role: 'Lead Instructor - Full Stack Java', 
    exp: '7+ years in Full Stack',
    track: 'Full Stack JAVA',
    students: '1.1K',
    rating: 4.9,
    desc: '7+ years in Full Stack development',
    bio: 'Expert in Java full stack development with hands-on experience in building scalable web applications.',
    image: 'https://xceededge.com/data/images/gangadhar.jpeg'
  },
  { 
    name: 'B.R. Babu', 
    role: 'Lead Instructor - Full Stack Python', 
    exp: '6+ years in Full Stack',
    track: 'Full Stack Python',
    students: '1.8K',
    rating: 4.8,
    desc: '6+ years in Full Stack development',
    bio: 'Specialist in Python full stack technologies including modern frameworks and backend systems.',
    image: 'https://xceededge.com/data/images/B.R.Babu.jpeg'
  },
  { 
    name: 'V Praveen kumar', 
    role: 'Lead Instructor - Gen AI & Agentic AI', 
    exp: '9+ years in Gen AI & Agentic AI',
    track: 'Gen AI & Agentic AI',
    students: '1.5K',
    rating: 4.9,
    desc: '9+ years in Generative AI & autonomous systems',
    bio: 'Specializes in Generative AI, LLMs, and autonomous AI agents with real-world implementation experience.',
    image: '' 
  },
  { 
    name: 'Ch.Sruthi', 
    role: 'Chief AI Instructor (AIML)', 
    exp: '12+ years in AI & ML',
    track: 'AI & Future Tech',
    students: '3.2K',
    rating: 5.0,
    desc: '12+ years in AI & Machine Learning',
    bio: 'Experienced AI practitioner focused on building production-grade ML systems and simplifying complex concepts.',
    image: '' 
  },
  { 
    name: 'Alekhya', 
    role: 'Head of SOC Training', 
    exp: '7+ years in SOC',
    track: 'SOC',
    students: '2.4K',
    rating: 4.9,
    desc: '7+ years in Security Operations Center',
    bio: 'SOC expert specializing in threat monitoring, SIEM tools, and real-time incident response.',
    image: 'https://xceededge.com/data/images/Alekhya.jpeg'
  },
  { 
    name: 'Roshan', 
    role: 'Lead Instructor - VAPT', 
    exp: '4+ years in VAPT',
    track: 'VAPT',
    students: '1.2K',
    rating: 4.9,
    desc: '4+ years in Vulnerability Assessment & Penetration Testing',
    bio: 'Cybersecurity trainer focused on ethical hacking, penetration testing, and real-world security practices.',
    image: '' 
  },
];

const values = [
  { icon: Target, title: 'Industry-Focused', desc: 'Every course is designed around real job roles and market demands.' },
  { icon: Zap, title: 'Hands-On Learning', desc: 'Learn by doing with live projects, labs, and real-world assignments.' },
  { icon: Award, title: 'Certified Excellence', desc: 'Industry-recognized certifications that employers trust.' },
  { icon: Heart, title: 'Student-First', desc: 'Dedicated mentorship, placement support, and lifetime access.' },
  { icon: Globe, title: 'Accessible', desc: 'Quality education available to every learner across India.' },
  { icon: Users, title: 'Community', desc: '50,000+ alumni network for networking and career opportunities.' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">About XceedEdge</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Empowering India's Next<br /><span className="text-primary">Tech Generation</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              XceedEdge is India's premier tech learning platform, built to bridge the gap between education and employment. 
              We offer job-ready courses in AI, Cybersecurity, Full Stack, and more taught by industry experts with real-world experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-card border-y">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: 'Students Enrolled', value: '5,000+' },
            { label: 'Placement Rate', value: '94%' },
            { label: 'Expert Instructors', value: '25+' },
            { label: 'Industry Partners', value: '10+' },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className="text-3xl font-heading font-bold text-primary mb-1">{s.value}</div>
              <div className="text-sm text-muted-foreground">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl font-bold mb-4">Our Mission</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We started XceedEdge with a simple belief: every motivated learner deserves access to world-class tech education, 
              regardless of their background or location.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our curriculum is designed by practitioners not just academicians. Every course includes live projects, 
              mentorship, and placement assistance to ensure you don't just learn, you launch your career.
            </p>
          </motion.div>
        <motion.div
  initial={{ opacity: 0, x: 30 }}
  whileInView={{ opacity: 1, x: 0 }}
  viewport={{ once: true }}
  className="bg-gradient-to-br from-primary/20 to-accent/10 rounded-2xl p-8 text-center"
>
  {/* Brand Impact */}
  <div className="text-5xl font-heading font-black text-primary mb-3">
    5,000+
  </div>
  <p className="text-muted-foreground">
    Students trained across Full Stack, AI & Cybersecurity
  </p>

  {/* Divider */}
  <div className="my-4 h-px bg-border/40" />

  {/* Outcome */}
  <div className="text-3xl font-heading font-bold text-foreground">
    94%+ Placement Success Rate
  </div>
  <p className="text-muted-foreground mt-1 text-sm">
    Supported by hands-on training, real-world projects & mentor guidance
  </p>

  {/* Secondary Proof */}
  <div className="mt-6 text-xl font-semibold text-primary">
    ₹3.5L → ₹8L+ Avg Salary Growth
  </div>
</motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-3">What We Stand For</h2>
            <p className="text-muted-foreground">The principles that guide everything we do</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 border hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team - UPDATED TO SHOW ALL DETAILS */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-3">Meet Our Experts</h2>
            <p className="text-muted-foreground">Learn from professionals who have built systems at scale</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((member, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 border hover:shadow-lg transition-all flex flex-col h-full">
                
                {/* Header: Avatar, Name, Role */}
                <div className="flex items-center gap-4 mb-4">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-16 h-16 rounded-full object-cover border-2 border-primary/20" />
                  ) : (
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shrink-0">
                      <span className="text-2xl font-heading font-bold text-white">{member.name[0]}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading font-bold text-lg leading-tight">{member.name}</h3>
                    <p className="text-sm text-primary font-semibold mt-1">{member.role}</p>
                  </div>
                </div>

                {/* Bio */}
                <p className="text-sm text-muted-foreground mb-6 flex-grow">{member.bio}</p>

                {/* Footer Stats: Rating, Students, Exp */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border/50">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>{member.rating} / 5.0</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span>{member.students} Students</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium col-span-2">
                    <Briefcase className="w-4 h-4 text-emerald-500" />
                    <span>{member.exp}</span>
                  </div>
                </div>

              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}